import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Grid, InputAdornment } from '@mui/material'
import ComponentApi from 'api/components-api'
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import * as UseStyled from 'features/report/useStyled'
import _ from 'lodash'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'
import TextInput from 'new-components/inputs/text-input'
import Loading from 'new-components/loading/loading'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'
import { getBranch } from 'utils/app.utils'
import PatientTypesList from './PatientTypesList'

const PatientTypes = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const componentRef = useRef<HTMLDivElement>(null)
    const [branchId, setBranchId] = useState<any>(getBranch())
    const [branches, setBranches] = useState<any[]>([])
    const [branchSelect, setBranchSelect] = useState<number>(getBranch())
    const [rangeDate, setRangeDate] = useState<string[]>(emptyRangePicker)
    const [rowCount, setRowCount] = useState<number>(0)
    const [sortBy, setSortBy] = useState<string>('createAt')
    const [sortType, setSortType] = useState<string>('ASC')
    const [fetch, setFetch] = useState(false)
    const [rows, setRows] = useState<any[]>([])
    const [rowsExport, setRowsExport] = useState<any[]>([])
    const [rowsGroup, setRowsGroup] = useState<any[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [patientTypeOption, setPatientTypeOption] = useState<{ name: string, id: any }[]>([])
    const [patientTypeSelect, setPatientTypeSelect] = useState<string[]>([])
    const [search, setSearch] = useState<string>('');
    const [staticCondition, setStaticCondition] = useState({
        page: 1,
        sortBy: sortBy,
        sortType: sortType,
        branchId,
        patientTypeId: patientTypeSelect?.join(',') || ''
    });
    const [pageLimit, setPageLimit] = useState(10)
    const [isExport, setIsExport] = useState(true)
    const verifyPermission = async () => {
        const condition: FindAllReportInterface = {
            page: 1,
            pageLimit: 200
        }
        ReportApi.reportList(condition).then(({ data }) => {
            const check = _.find(data, (val: any) => String(val.reportKey) === 'PATIENT_TYPES') ? true : false
            if (!check) {
                history.push(routeName.report)
                return false
            }
        })
        ClinicApi.findAllBranches()
            .then(({ data }) => setBranches(data.map((item: any) => ({ name: item?.branchName, value: item?.branchId }))))
            .catch((e) => {
                return
            })
        ComponentApi.findAllPatientType(condition).then((resPatientTypes) => {
            if (resPatientTypes.status === 200) {
                const newData = resPatientTypes?.data.map((item: any) => {
                    return { name: item?.patientTypeName, id: item?.patientTypeId }
                })
                setPatientTypeOption(newData)
                setPatientTypeSelect(newData.map((item: any) => item.id))
            }
        })
    }
    useEffect(() => {
        verifyPermission()
    }, [branchId])

    const fetchDF = async (isExport?: boolean) => {
        let condition: any = { ...staticCondition, pageLimit: pageLimit, search: search };
        if (isExport) condition = { ...condition, export: '1' };
        setLoading(true);
        ReportApi.reportPatientTypeOption(condition)
            .then(({ headers, data }) => {
                if (!isExport) {
                    setRows(data?.rows || []);
                    setRowCount(headers['x-total']);
                } else {
                    setIsExport(false)
                    setRowsExport(data?.rows || []);
                    setRowsGroup(data?.group || [])
                }
            })
            .catch((e) => console.error(e))
            .finally(() => {
                setLoading(false);
            });
    };
    const { onDownload } = useDownloadExcel({
        tableExportRef: componentRef.current,
        filename: `${t(`REPORT.REPORT_NAME.PATIENT_TYPES`)}`,
        sheetName: `${t(`REPORT.REPORT_NAME.PATIENT_TYPES`)}`
    })
    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }
    const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)
    const handleChangePage = (val: number) => handlePageChange(val)


    useEffect(() => {
        isExport && fetchDF(true)
    }, [isExport, staticCondition])


    useEffect(() => {
        fetchDF()
    }, [staticCondition, pageLimit])


    const handlePageChange = (newPage: number) => {
        setStaticCondition({
            ...staticCondition,
            page: newPage
        });
    };
    const handleButtonClick = () => {
        const newPatientType = patientTypeSelect.length === patientTypeOption?.length ? [] : patientTypeSelect
        setStaticCondition({
            ...staticCondition,
            page: 1,
            branchId: branchSelect,
            patientTypeId: newPatientType?.join(',') || ''
        });
        setIsExport(true)
    };
    const renderPatientTypeSelect = useCallback((): string => {
        if (patientTypeOption?.length === patientTypeSelect?.length || !patientTypeSelect?.length) {
            return t('ALL')
        }
        const newText = patientTypeSelect.map((item: any) => patientTypeOption.find((obj: any) => obj.id === item)?.name).join(',')
        return newText
    }, [patientTypeOption, patientTypeSelect])

    return (
        <Box >
            <HeaderCard text={t('REPORT.REPORT_NAME.PATIENT_TYPES')} />
            <Box sx={{ px: 3, pt: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3} xl={2} sx={{ pt: 2 }}>
                        <TextInput
                            fullWidth
                            placeholder={t('REPORT.TABLE.PATIENT_TYPES.SEARCH')}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2} sx={{ pt: 2 }}>
                        <SelectInput
                            label={t('NAV.BRANCH')}
                            value={branchSelect.toString()}
                            onChange={e => setBranchSelect(e.target.value)}
                            options={branches}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2} sx={{ pt: 2 }}>
                        <SelectCheckBoxInput
                            label={t('REPORT.TABLE.PATIENT.PATIENT_TYPE')}
                            selected={patientTypeSelect}
                            onChange={setPatientTypeSelect}
                            options={[{ id: '0', name: 'ไม่ระบุประเภทผู้ป่วย' }, ...patientTypeOption]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={1} xl={1} sx={{ pt: 2 }}>
                        <MainButton
                            title={t('INPUT.SEARCH')}
                            type="primary"
                            variant="contained"
                            onClick={handleButtonClick}
                            style={{ width: '120px' }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2} xl={5} sx={{ pt: 2, display: 'flex', justifyContent: 'flex-end' }} >
                        <UseStyled.DropdownDocument>
                            <Dropdown.Toggle id="dropdown-basic">
                                {t('REGISTER.BUTTON.PRINT_DOCUMENT')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={onDownload}>{t('Excel')}</Dropdown.Item>
                                <Dropdown.Item onClick={() => window.print()}>{t('PDF')}</Dropdown.Item>
                            </Dropdown.Menu>
                        </UseStyled.DropdownDocument>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3, px: 2, pb: 4 }}>
                    <PatientTypesList
                        page={staticCondition.page}
                        pageLimit={pageLimit}
                        rowCount={rowCount}
                        sortBy={staticCondition.sortBy}
                        sortType={staticCondition.sortType}
                        data={rows}
                        dataExport={rowsExport}
                        rowsGroup={rowsGroup}
                        onRequestSort={onRequestSort}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleChangePage={handleChangePage}
                        setSortType={setSortType}
                        setSortBy={setSortBy}
                        componentRef={componentRef}
                        branchName={_.get(_.find(branches, { value: branchSelect }), 'name')}
                        rangeDate={rangeDate}
                        patientTypeSelect={renderPatientTypeSelect}
                    />
                </Box>
            </Box>

            {loading && <Loading />}
        </Box >
    );


}
export default PatientTypes